'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { COUNTRY_CODES, JOB_TITLES } from '@/utils/constants';
import { employeeApi, type PutEmployeeBody } from '@/api/employee';
import { Employee } from '@/types/employee';

interface EmployeeState {
  data: Employee | null;
  isLoading: boolean;
  error: string | null;
}

interface EmployeeContextType {
  employee: EmployeeState;
  countryCodeOptions: typeof COUNTRY_CODES;
  jobTitleOptions: typeof JOB_TITLES;
  setEmployee: (employee: Employee | null) => void;
  fetchEmployee: () => Promise<void>;
  updateEmployee: (data: Pick<PutEmployeeBody, 'name' | 'phone' | 'countryCode'>) => Promise<void>;
  getAuthURL: () => Promise<string>;
  setAuthToken: (code: string) => Promise<string>;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

export function EmployeeProvider({ children }: { children: React.ReactNode }) {
  const [employee, setEmployeeState] = useState<EmployeeState>({
    data: null,
    isLoading: false,
    error: null,
  });
  const { toast } = useToast();

  const setEmployee = useCallback((employee: Employee | null) => {
    setEmployeeState(prev => ({ ...prev, data: employee }));
  }, []);

  const fetchEmployee = useCallback(async () => {
    setEmployeeState(prev => ({ ...prev, isLoading: true }));
    try {
      const response = await employeeApi.getEmployee();
      setEmployeeState({
        data: response.employee,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setEmployeeState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch employee data',
      }));
      toast({
        description: error instanceof Error ? error.message : 'Failed to fetch employee data',
        variant: 'error',
      });
    }
  }, [toast]);

  const updateEmployee = useCallback(
    async (data: Pick<PutEmployeeBody, 'name' | 'phone' | 'countryCode'>) => {
      setEmployeeState(prev => ({ ...prev, isLoading: true }));
      try {
        const id = employee.data?.id;
        if (!id) {
          throw new Error('Employee not loaded');
        }
        const response = await employeeApi.updateEmployee({ id, data });
        setEmployeeState({
          data: response.employee,
          isLoading: false,
          error: null,
        });
        toast({
          description: 'Profile updated successfully',
        });
      } catch (error) {
        setEmployeeState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to update employee data',
        }));
        toast({
          description: error instanceof Error ? error.message : 'Failed to update employee data',
          variant: 'error',
        });
        throw error;
      }
    },
    [toast, employee.data?.id]
  );

  const getAuthURL = async () => {
    try {
      const response = await employeeApi.getAuthURL();
      return response.url;
    } catch (error) {
      console.error('Error getting Google auth URL:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to get Google auth URL',
        variant: 'error',
      });
      throw error;
    }
  };

  const setAuthToken = async (code: string) => {
    try {
      const response = await employeeApi.setAuthToken(code);
      return response.tokens;
    } catch (error) {
      console.error('Error setting Google auth token:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to set Google auth token',
        variant: 'error',
      });
      throw error;
    }
  };

  return (
    <EmployeeContext.Provider
      value={{
        employee,
        countryCodeOptions: COUNTRY_CODES,
        jobTitleOptions: JOB_TITLES,
        setEmployee,
        fetchEmployee,
        updateEmployee,
        getAuthURL,
        setAuthToken,
      }}
    >
      {children}
    </EmployeeContext.Provider>
  );
}

export const useEmployee = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};
